import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useState } from 'react'
import { Link, useLocation } from 'wouter'
import { Mail, Lock, Eye, EyeOff, CheckCircle, Users, FileText, Zap, Shield, TrendingUp, Globe, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '@/contexts/auth-context'
import { buildApiUrl } from '@/config/api'

export function Login() {
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLinkedInLoading, setIsLinkedInLoading] = useState(false)
  const [, setLocation] = useLocation()
  const { login } = useAuth()
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    const result = await login(formData.email, formData.password)

    if (result.success) {
      toast.success(result.message)
      // Small delay to ensure state is properly set
      setTimeout(() => {
        setLocation('/')
      }, 100)
    } else {
      toast.error(result.message)
    }

    setIsLoading(false)
  }

  const handleLinkedInLogin = async () => {
    setIsLinkedInLoading(true)
    try {
      const response = await fetch(buildApiUrl('/api/auth/linkedin/url'))
      const data = await response.json()
      
      if (data.success) {
        window.location.href = data.data.authUrl
      } else {
        toast.error('Failed to initiate LinkedIn login')
      }
    } catch (error) {
      console.error('LinkedIn login error:', error)
      toast.error('Failed to connect to LinkedIn')
    } finally {
      setIsLinkedInLoading(false)
    }
  }

  const features = [
    {
      icon: <Zap className="h-6 w-6" />,
      title: "AI-Powered Content",
      description: "Generate professional RFPs and responses with OpenAI integration"
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Network & Connect",
      description: "Build relationships with suppliers and procurement professionals"
    },
    {
      icon: <FileText className="h-6 w-6" />,
      title: "Smart Management",
      description: "Organize and track all your RFPs and responses in one place"
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Secure & Reliable",
      description: "Enterprise-grade security with encrypted data protection"
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: "Analytics & Insights",
      description: "Track performance and optimize your procurement process"
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: "Global Reach",
      description: "Connect with suppliers worldwide and expand your network"
    }
  ]

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Marketing Content */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary/10 via-primary/5 to-background p-8 lg:p-12">
        <div className="max-w-lg mx-auto flex flex-col justify-center">
          {/* Logo and Brand */}
          <div className="mb-8">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-xl bg-gradient-to-r from-primary to-primary/80 mb-6">
              <span className="text-white font-bold text-2xl">SI</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Buyers and Sellers
              <span className="text-primary block">Matching Platform</span>
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Transform your procurement process with B2B matchmaking between buyers and sellers. The comprehensive RFP management platform that connects procurement professionals with the right suppliers, powered by AI. SourcedIn matched buyers with suppliers in 100+ countries.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Current RFPs Section */}
          <div className="bg-gradient-to-r from-primary/5 to-primary/10 backdrop-blur-sm rounded-xl p-6 border border-primary/20 mb-6">
            <div className="flex items-center space-x-4 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center text-primary">
                <Search className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-foreground">See current RFPs waiting to be matched</p>
                <p className="text-sm text-muted-foreground">Discover active procurement opportunities in the marketplace</p>
              </div>
            </div>
            <Link href="/request-for-proposal-marketplace">
              <Button variant="outline" className="w-full bg-white/80 hover:bg-white border-primary/30 hover:border-primary text-primary hover:text-primary transition-all">
                <Search className="mr-2 h-4 w-4" />
                Browse Open Marketplace
              </Button>
            </Link>
          </div>

          {/* Social Proof */}
          <div className="bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-white/20">
            <div className="flex items-center space-x-4 mb-4">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-r from-primary/20 to-primary/40 border-2 border-white"></div>
                ))}
              </div>
              <div>
                <p className="font-semibold text-foreground">Join 500+ professionals</p>
                <p className="text-sm text-muted-foreground">Trusted by procurement teams worldwide</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Free plan available</span>
              <span>•</span>
              <span>No credit card required</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center p-4 lg:p-8">
        {/* Inspirational Quote - Desktop Only */}
        <div className="hidden lg:block mb-8 text-center">
          <div className="max-w-md mx-auto">
            {/* Professional Business Woman Image */}
            <div className="mb-6">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary/20 to-primary/10 border-2 border-primary/20 overflow-hidden">
                <img 
                  src="/images/business-woman-quote.jpg" 
                  alt="Sarah Chen, Chief Procurement Officer"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            <div className="text-3xl text-primary mb-4">"</div>
            <blockquote className="text-lg font-medium text-foreground mb-4 leading-relaxed">
              Success in procurement isn't about finding the cheapest option—it's about finding the right partner who delivers exceptional value.
            </blockquote>
            <cite className="text-sm text-muted-foreground font-medium">
              — Sarah Chen, Chief Procurement Officer at TechCorp
            </cite>
          </div>
        </div>

        <div className="w-full max-w-md mx-auto">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-lg bg-gradient-to-r from-primary to-primary/80 mb-4">
              <span className="text-white font-bold text-lg">SI</span>
            </div>
            <h2 className="text-2xl font-bold text-foreground">Welcome back</h2>
            <p className="text-muted-foreground">Sign in to your SourcedIn account</p>
          </div>

          <Card className="border-0 shadow-none lg:shadow-lg">
            <CardHeader className="text-center lg:text-left">
              <CardTitle className="text-2xl lg:text-xl">Sign In</CardTitle>
              <CardDescription>
                Access your RFP management dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="Enter your email"
                      className="w-full pl-10 pr-4 py-3 border border-input rounded-lg bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Enter your password"
                      className="w-full pl-10 pr-10 py-3 border border-input rounded-lg bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <div className="mt-2 text-right">
                    <Link href="/reset-password-request" className="text-sm text-primary hover:underline">
                      Forgot your password?
                    </Link>
                  </div>
                </div>
                <Button type="submit" className="w-full py-3 text-base font-medium" disabled={isLoading}>
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>

              {/* LinkedIn OAuth Button */}
              <Button
                type="button"
                variant="outline"
                className="w-full py-3 bg-[#0077B5] text-white border-[#0077B5] hover:bg-[#006097] hover:border-[#006097] transition-colors"
                onClick={handleLinkedInLogin}
                disabled={isLinkedInLoading}
              >
                {isLinkedInLoading ? (
                  'Connecting to LinkedIn...'
                ) : (
                  <>
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    Continue with LinkedIn
                  </>
                )}
              </Button>

              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Don't have an account?{' '}
                  <Link href="/register" className="text-primary hover:underline font-medium">
                    Sign up for free
                  </Link>
                </p>
              </div>

              {/* Legal Links */}
              <div className="mt-4 text-center">
                <p className="text-xs text-muted-foreground">
                  By signing in, you agree to our{' '}
                  <Link href="/terms" className="text-primary hover:underline">
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link href="/privacy" className="text-primary hover:underline">
                    Privacy Policy
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
} 